import React from 'react';
import {
  CalendarRangeIcon,
  GlobeIcon,
  Link2Icon,
  LockIcon,
  MapPinIcon,
  UsersIcon,
  Building2Icon
} from 'lucide-react';

import { cn } from '@epoch-ui/lib/utils';
import { EditableEvent, EventType } from 'types/event.types';
import { getEventDateString, getEventTimeString, formatEventLocation } from 'utils/eventUtils';
import {
  DataViewLine,
  DataViewLineIcon,
  DataViewLineContent,
  DataViewLineHeading,
  DataViewLineSubText
} from '@epoch-ui/components/DataViewLine';

interface EventInfoProps extends React.HTMLAttributes<HTMLDivElement> {
  event: Partial<EditableEvent>;
}

const EventInfoContext = React.createContext<EventInfoProps['event'] | null>(null);

export const EventInfo: React.FC<EventInfoProps> = ({ event, children, className }) => {
  return (
    <EventInfoContext.Provider value={event}>
      <div className={cn('tw-flex tw-flex-col tw-gap-3', className)}>{children}</div>
    </EventInfoContext.Provider>
  );
};

const useEventInfo = (): EventInfoProps['event'] => {
  const context = React.useContext(EventInfoContext);
  if (!context) {
    throw new Error('useEventInfo must be used within an EventInfo component');
  }
  return context;
};

export const EventInfoSchedule = (): React.ReactNode => {
  const event = useEventInfo();

  return (
    <DataViewLine>
      <DataViewLineIcon>
        <CalendarRangeIcon size={20} />
      </DataViewLineIcon>
      <DataViewLineContent>
        <DataViewLineHeading>{getEventDateString(event)}</DataViewLineHeading>
        <DataViewLineSubText>
          {event.eventType === EventType.MULTI_SESSIONS
            ? `${event.sessions?.length ?? 0} session${event.sessions?.length === 1 ? '' : 's'}`
            : getEventTimeString(event)}
        </DataViewLineSubText>
      </DataViewLineContent>
    </DataViewLine>
  );
};

export const EventInfoAddress = (): React.ReactNode => {
  const event = useEventInfo();

  if (!event.isInPerson) {
    return null;
  }

  return (
    <DataViewLine>
      <DataViewLineIcon>
        <MapPinIcon size={20} />
      </DataViewLineIcon>
      <DataViewLineContent>
        <DataViewLineHeading>Place or address</DataViewLineHeading>
        <DataViewLineSubText>
          {formatEventLocation({
            ...event,
            isInPerson: true,
            isVirtual: false
          })}
        </DataViewLineSubText>
      </DataViewLineContent>
    </DataViewLine>
  );
};

export const EventInfoVirtualLink = (): React.ReactNode => {
  const event = useEventInfo();

  if (!event.isVirtual) {
    return null;
  }

  const virtualLocation = event.virtualEventInfo?.virtualLocation;
  const virtualMeetingLink =
    virtualLocation && !virtualLocation.startsWith('http')
      ? `https://${virtualLocation}`
      : virtualLocation;

  return (
    <DataViewLine>
      <DataViewLineIcon>
        <Link2Icon size={20} />
      </DataViewLineIcon>
      <DataViewLineContent>
        <DataViewLineHeading>Virtual link</DataViewLineHeading>
        <DataViewLineSubText>{virtualMeetingLink ?? 'No virtual link yet'}</DataViewLineSubText>
      </DataViewLineContent>
    </DataViewLine>
  );
};

export const EventInfoPrivacy = (): React.ReactNode => {
  const event = useEventInfo();

  return (
    <DataViewLine>
      <DataViewLineIcon>
        {event.isPrivate ? <LockIcon size={20} /> : <GlobeIcon size={20} />}
      </DataViewLineIcon>
      <DataViewLineContent>
        <DataViewLineHeading>{event.isPrivate ? 'Private' : 'Public'}</DataViewLineHeading>
        <DataViewLineSubText>
          {event.isPrivate ? 'Only invited guests can see this event' : 'Anyone can see this event'}
        </DataViewLineSubText>
      </DataViewLineContent>
    </DataViewLine>
  );
};

export const EventInfoTeam = (): React.ReactNode => {
  const event = useEventInfo();

  if (!event.hostOrganization?.name) {
    return null;
  }

  return (
    <DataViewLine>
      <DataViewLineIcon>
        <UsersIcon size={20} />
      </DataViewLineIcon>
      <DataViewLineContent>
        <DataViewLineHeading>Team</DataViewLineHeading>
        <DataViewLineSubText>{event.hostOrganization?.name}</DataViewLineSubText>
      </DataViewLineContent>
    </DataViewLine>
  );
};

export const EventInfoLocation = (): React.ReactNode => {
  const event = useEventInfo();

  if (!event.companyLocation?.name) {
    return null;
  }

  return (
    <DataViewLine>
      <DataViewLineIcon>
        <Building2Icon size={20} />
      </DataViewLineIcon>
      <DataViewLineContent>
        <DataViewLineHeading>Location</DataViewLineHeading>
        <DataViewLineSubText>{event.companyLocation?.name}</DataViewLineSubText>
      </DataViewLineContent>
    </DataViewLine>
  );
};
